/*
 * viewport.js -- sdileny viewportovy zaklad chrome sablony terrahunt-2026.
 *
 * CO TENHLE MODUL DELA: drzi jedno referencni cislo -- vysku viewportu
 * NAMERENOU VE STAVU BEZ KLAVESNICE (`base`) -- a umi ho na pozadani
 * prepocitat.
 *
 * CO NEDELA: nedetekuje klavesnici, neridi dock, nesahá na tridy. To
 * staví 07-04 (dock.js) NAD timhle zakladem: `visualViewport.height <
 * base * 0.8` je prah klavesnice, ale porovnavat je proti cemu ma smysl
 * jen tehdy, kdyz `base` nekdo drzi na jednom miste a obnovuje ho pri
 * otoceni zarizeni. Kdyby si kazdy modul mericí vysku pamatoval vlastni
 * baseline, po otoceni telefonu by se rozesly.
 *
 * FEATURE-DETECT (kontrakt B): `window.visualViewport` je Chrome 61+ /
 * Safari 13+ / Firefox 91+, tedy cela matice podpory ho ma. Fallback na
 * `window.innerHeight` tu presto je -- degradace "vse viditelne" nesmi
 * zaviset na tom, ze matice zustane navzdy stejna.
 *
 * POZOR na `orientationchange`: prohlizec ho vystreli JESTE PRED tim, nez
 * prepocita metriky viewportu (na iOS mereno opakovane). Prvni mereni
 * proto muze vratit starou hodnotu a modul meri po chvili znovu. Bez toho
 * by po otoceni telefonu na sirku zustal `base` z vysky, prah 0,8 by sedel
 * trvale pod skutecnou vyskou a dock by se tvaril, ze je otevrena
 * klavesnice.
 */

/** Kolik ms po orientationchange se mereni opakuje (viz docblock). */
const ORIENTATION_SETTLE_MS = 300;

/**
 * Jak dlouho po posledni `resize` udalosti se ceka na ustaleni (07-07,
 * bod 11).
 *
 * `resize` chodi behem tazeni okna po desitkach za sekundu; prepocitavat
 * baseline pri kazde z nich by znamenalo merit prubeh tazeni, ne vysledek.
 */
const RESIZE_SETTLE_MS = 200;

/** Prvky, pri fokusu v nichz se baseline NEPREPOCITAVA (viz onResize). */
const EDITABLE = 'input,textarea,select,[contenteditable]';

let base = 0;
let started = false;
let resizeTimer = null;

/**
 * Aktualni vyska viditelne casti stranky.
 *
 * @return {number} Vyska v CSS pixelech.
 */
function height() {
	return window.visualViewport ? window.visualViewport.height : window.innerHeight;
}

/**
 * Premeri a ulozi referencni vysku (`base`).
 *
 * @return {number} Nova referencni vyska.
 */
function refresh() {
	base = height();
	return base;
}

/**
 * Referencni vyska bez klavesnice.
 *
 * Kdyby se na ni nekdo zeptal driv, nez modul nastartoval, zmeri se
 * rovnou -- nula vracena "protoze init jeste nebezel" by byla tise
 * spatna hodnota, se kterou by kazdy prah vysel.
 *
 * @return {number}
 */
function getBase() {
	if (!started) {
		return refresh();
	}
	return base;
}

/**
 * Nastartuje modul: prvni mereni + obnova pri otoceni zarizeni.
 *
 * Volani je idempotentni -- druhy `init()` uz jen premeri, neposchazi
 * druhy posluchac.
 *
 * @return {number} Referencni vyska po prvnim mereni.
 */
/**
 * Je fokus v editovatelnem prvku, tedy pravdepodobne otevrena klavesnice?
 *
 * @return {boolean}
 */
function isTyping() {
	const active = document.activeElement;

	return !!(active && active.matches && active.matches(EDITABLE));
}

/**
 * Premeri baseline, ale jen kdyz se prave nepise (WR-01).
 *
 * @return {void}
 */
function refreshIfNotTyping() {
	if (isTyping()) {
		return;
	}
	refresh();
}

/**
 * Ustaleny `resize` -- prepocet baseline, ale JEN kdyz nejde o klavesnici
 * (07-07, bod 11).
 *
 * Do 07-06 se baseline obnovovala jen pri initu a pri `orientationchange`.
 * Po skutecne zmene velikosti okna (rotace tabletu bez `orientationchange`,
 * split-screen na Androidu, zmena velikosti okna na desktopu) tak zustala
 * stara hodnota a prah 0,8 lezel trvale pod skutecnou vyskou -- dock se
 * choval, jako by byla otevrena klavesnice.
 *
 * PROC PODMINKA NA FOKUS: virtualni klavesnice vystreli `resize` uplne
 * stejne jako zmena okna. Kdyby se baseline prepocitala PRAVE TEHDY,
 * ulozila by se zmensena vyska jako referencni a detekce klavesnice by uz
 * nikdy nesepnula -- gate by pritom svitil zelene, protoze by se meril proti
 * spatne referenci.
 *
 * @return {void}
 */
/*
 * ZAMERNA VYJIMKA Z "HEIGHT-ONLY IGNOROVAT" (1.27.13, koordinatoruv
 * pozadavek na `dock.js`/`carousel.js`/`map.js`): TENHLE modul height-only
 * zmeny ignorovat NESMI, ze dvou duvodu:
 *   1. `refresh()` jen prepise lokalni cislo `base` -- ZADNA classList/
 *      atribut/style mutace, nic, co by MutationObserver zaznamenal nebo
 *      co by WebKit v hover fazi tapu mohl vzit jako "zmenu obsahu". Neni
 *      soucasti mechanismu, ktery klik zahazuje.
 *   2. Detekce klavesnice (`dock.js measureKeyboard()`) POROVNAVA aktualni
 *      vysku proti tomuhle `base` -- a otevreni klavesnice JE svou
 *      podstatou zmena VYSKY beze zmeny SIRKY. Kdyby `onResize()`
 *      ignoroval height-only zmeny, prestala by se baseline kdy
 *      po skutecne zmene vysky (napr. trvale schovana lista adresy)
 *      prepocitavat a prah `0.8 * base` by se casem rozesel se skutecnosti.
 */
function onResize() {
	if (resizeTimer) {
		window.clearTimeout(resizeTimer);
	}

	resizeTimer = window.setTimeout(function () {
		resizeTimer = null;
		refreshIfNotTyping();
	}, RESIZE_SETTLE_MS);
}

function init() {
	if (started) {
		return refresh();
	}

	started = true;
	refresh();

	/*
	 * TATAZ POJISTKA JAKO U `resize` (WR-01 z code review 07-07).
	 *
	 * `orientationchange` mel az do teto opravy `refresh()` bezpodminecny,
	 * takze otoceni telefonu PRI PSANI do pole ulozilo jako referencni vysku
	 * tu ZMENSENOU klavesnici -- a `dock.js` uz pak prah `getBase() * 0,8`
	 * nikdy neprekrocil, takze detekce klavesnice do konce zivota stranky
	 * mlcela. Psani na sirku je bezny scenar, ne exoticky.
	 *
	 * Baseline se dorovna pri prvni dalsi prilezitosti bez fokusu v poli
	 * (`resize` s debounce), takze se o mereni nepricha -- jen se neulozi
	 * v okamziku, kdy je zarucene spatne.
	 */
	window.addEventListener('orientationchange', function () {
		refreshIfNotTyping();
		window.setTimeout(refreshIfNotTyping, ORIENTATION_SETTLE_MS);
	});

	window.addEventListener('resize', onResize);

	return base;
}

export { init, refresh, getBase, height };
